import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faExclamationTriangle, 
    faTimes, 
    faShieldAlt,
    faBullhorn,
    faBan,
    faExclamationCircle,
    faSkullCrossbones,
    faAngry,
    faEllipsisH
} from '@fortawesome/free-solid-svg-icons';
import '../styles/modal.css';

const ReportModal = ({ show, onClose, onSubmit, targetType, targetName }) => {
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const reportReasons = [
        { value: 'harassment', label: 'Harcèlement ou intimidation', icon: faExclamationCircle, color: '#c51618' },
        { value: 'spam', label: 'Spam ou contenu indésirable', icon: faBullhorn, color: '#f39c12' },
        { value: 'explicit', label: 'Contenu explicite ou inapproprié', icon: faBan, color: '#e74c3c' },
        { value: 'fake', label: 'Faux profil ou arnaque', icon: faExclamationTriangle, color: '#e67e22' },
        { value: 'violence', label: 'Violence ou contenu dangereux', icon: faSkullCrossbones, color: '#c0392b' },
        { value: 'hate', label: 'Discours haineux', icon: faAngry, color: '#8e44ad' },
        { value: 'other', label: 'Autre', icon: faEllipsisH, color: '#95a5a6' },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!reason) {
            alert('Veuillez sélectionner une raison');
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit({ reason, description });
            // Reset form
            setReason('');
            setDescription('');
        } catch (error) {
            console.error('Error submitting report:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setReason('');
        setDescription('');
        onClose();
    };

    if (!show) {
        return null;
    }

    const targetTypeLabels = {
        product: 'cette annonce',
        message: 'ce message',
        profile: 'ce profil',
        review: 'cet avis',
    };

    const selectedReasonData = reportReasons.find(r => r.value === reason);

    return (
        <div className="modal-overlay report-modal-overlay" onClick={handleClose}>
            <div className="modal-pop report-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header report-modal-header">
                    <div className="report-modal-title-section">
                        <FontAwesomeIcon icon={faShieldAlt} className="report-shield-icon" />
                        <h2 className="modal-title">Signaler {targetTypeLabels[targetType] || 'ce contenu'}</h2>
                    </div>
                    <button className="modal-close report-close-btn" onClick={handleClose} aria-label="Fermer">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-content-pop report-modal-content">
                        {targetName && (
                            <div className="report-target-info">
                                <p><strong>{targetName}</strong></p>
                            </div>
                        )}
                        
                        <div className="report-form-group">
                            <label className="report-label">
                                Raison du signalement <span className="required-asterisk">*</span>
                            </label>
                            <div className="report-reasons-grid">
                                {reportReasons.map(r => (
                                    <div
                                        key={r.value}
                                        className={`report-reason-card ${reason === r.value ? 'selected' : ''}`}
                                        onClick={() => setReason(r.value)}
                                        style={{
                                            borderColor: reason === r.value ? r.color : '#e0e0e0'
                                        }}
                                    >
                                        <div className="report-reason-icon" style={{ color: r.color }}>
                                            <FontAwesomeIcon icon={r.icon} />
                                        </div>
                                        <span className="report-reason-label">{r.label}</span>
                                        {reason === r.value && (
                                            <div className="report-reason-checkmark" style={{ backgroundColor: r.color }}>
                                                ✓
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="report-form-group">
                            <label className="report-label">
                                Détails supplémentaires (optionnel)
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Décrivez le problème en détail pour nous aider à mieux comprendre la situation..."
                                rows={4}
                                className="report-textarea"
                                maxLength={500}
                            />
                            <span className="report-char-count">{description.length}/500</span>
                        </div>

                        <div className="report-info-box">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="report-info-icon" />
                            <p>Notre équipe examinera votre signalement dans un délai de 24 heures. Tous les signalements sont traités de manière confidentielle.</p>
                        </div>
                    </div>
                    <div className="modal-actions report-modal-actions">
                        <button
                            type="button"
                            className="modal-button report-cancel-btn"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="modal-button report-submit-btn"
                            disabled={isSubmitting || !reason}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-small"></span>
                                    Envoi...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faShieldAlt} />
                                    Envoyer le signalement
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

ReportModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    targetType: PropTypes.oneOf(['product', 'message', 'profile', 'review']).isRequired,
    targetName: PropTypes.string,
};

export default ReportModal;
